import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { scrollToPageTop } from "@/lib/scroll";

// Present-day Popoyo / Tola coast photos (self-hosted under /history, same set
// used on /history). Brought in directly so the home page teaser can render
// without importing the much larger HistorySection bundle.
const SLIDES = [
  "/history/coast-panorama.jpg",
  "/history/panga-sand.jpg",
  "/history/surfer-sunset.jpg",
  "/history/cattle-road.jpg",
  "/history/surfers-wave.jpg",
  "/history/aerial-waves.jpg",
];

export default function HistoryTeaser() {
  const { t } = useI18n();
  const [i, setI] = useState(0);

  // Auto-advance every 5s. Each slide is rendered as an absolutely-positioned
  // layer; opacity drives a smooth cross-fade between them.
  useEffect(() => {
    const t = setInterval(() => setI((c) => (c + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      data-testid="history-teaser"
      className="relative w-full overflow-hidden rounded-3xl"
      style={{ minHeight: 320 }}
    >
      {/* Stacked photo layers — only the active index is fully opaque */}
      <div className="absolute inset-0">
        {SLIDES.map((src, idx) => (
          <div
            key={src}
            aria-hidden
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url(${src})`,
              backgroundSize: "cover",
              backgroundPosition: "center 55%",
              opacity: idx === i ? 1 : 0,
              transform: idx === i ? "scale(1)" : "scale(1.04)",
              transition: "opacity 1100ms ease-in-out, transform 6000ms ease-out",
            }}
          />
        ))}
      </div>

      {/* Dark gradient overlay so the headline stays readable on any slide */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(27,59,111,0.45) 0%, rgba(27,59,111,0.55) 55%, rgba(27,59,111,0.88) 100%)",
        }}
      />

      <div className="relative px-6 sm:px-10 py-10 md:py-14 flex flex-col h-full">
        <div className="flex items-center gap-2 text-white/90">
          <BookOpen className="w-4 h-4" />
          <p className="eyebrow !text-white" style={{ color: "#fff", textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}>
            {t("history_eyebrow")}
          </p>
        </div>
        <h2
          className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl"
          style={{ fontFamily: "Outfit", textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
          data-testid="history-teaser-title"
        >
          {t("history_title")}
        </h2>
        <p
          className="mt-3 text-sm sm:text-base text-white/95 max-w-2xl"
          style={{ textShadow: "0 1px 14px rgba(0,0,0,0.45)" }}
        >
          {t("history_subtitle")}
        </p>
        <div className="mt-6 flex items-center gap-3">
          <Link
            to="/history"
            data-testid="history-teaser-cta"
            onClick={scrollToPageTop}
            className="inline-flex items-center gap-2 pill px-5 py-2.5 bg-white text-[color:var(--navy)] hover:bg-white/95 font-semibold shadow-md"
          >
            {t("history_cta")}
            <ArrowRight className="w-4 h-4" />
          </Link>
          {/* Dots indicator */}
          <div className="flex items-center gap-1.5" aria-hidden>
            {SLIDES.map((_, idx) => (
              <span
                key={idx}
                className="w-1.5 h-1.5 rounded-full transition-all"
                style={{
                  background: idx === i ? "#ffffff" : "rgba(255,255,255,0.45)",
                  width: idx === i ? 14 : 6,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
